import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ChevronRight,
  Eye,
  Sparkles,
  ArrowRight,
  Star,
  Mail,
  Crown,
  TrendingUp,
  Compass,
  Flame
} from 'lucide-react';

/* ─── colour tokens (matching the reference dark UI) ─── */
const C = {
  gold: '#C89B3C',
  goldLight: '#E8C872',
  goldMuted: '#8B7A4A',
  green: '#5B8C6B',
  greenLight: '#7DB88D',
  rose: '#D4736E',
  roseMuted: '#8B5A5A',
  plum: '#8B5A6B',
  cream: '#F4EFE6',
  charcoal: '#2B2B2B',
  dark: '#1A1A1A',
  darkCard: '#1E1E1E',
  card: '#232323',
  cardBorder: '#353535',
  cardBorderLight: '#444',
};

/* palette for the 12 palace badges */
const palaceBadgeColors = [
  '#A8E6CF', '#D4E157', '#64B5F6', '#81C784', '#E8C872',
  '#FFB74D', '#CE93D8', '#B39DDB', '#F48FB1', '#EF9A9A',
  '#80CBC4', '#A5D6A7',
];

/* ─── Countdown Timer ─── */
const CountdownTimer = () => {
  const launchDate = new Date(Date.now() + 14 * 86400000);
  const [time, setTime] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const tick = () => {
      const diff = launchDate - new Date();
      if (diff <= 0) return;
      setTime({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="flex gap-4 md:gap-6 justify-center" data-testid="countdown-timer">
      {Object.entries(time).map(([label, val]) => (
        <div key={label} className="text-center">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl flex items-center justify-center text-3xl md:text-4xl font-bold"
            style={{ background: `${C.gold}15`, color: C.gold, border: `1px solid ${C.gold}30` }}>
            {String(val).padStart(2, '0')}
          </div>
          <p className="text-xs mt-2 uppercase tracking-widest" style={{ color: '#888' }}>{label}</p>
        </div>
      ))}
    </div>
  );
};

/* ─── Feature Card ─── */
const FeatureCard = ({ icon: Icon, title, desc, accent }) => (
  <Card className="border-0 group cursor-pointer hover:-translate-y-1 transition-all duration-500"
    style={{ background: C.card, border: `1px solid ${C.cardBorder}` }}
    data-testid={`feature-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-6">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
        style={{ background: `${accent}15` }}>
        <Icon className="w-6 h-6" style={{ color: accent }} />
      </div>
      <h3 className="text-white font-semibold text-lg mb-2">{title}</h3>
      <p className="text-sm leading-relaxed" style={{ color: '#999' }}>{desc}</p>
    </CardContent>
  </Card>
);

/* ─── Wealth Bar ─── */
const WealthBar = ({ name, score, desc }) => (
  <div className="mb-8">
    <div className="flex justify-between items-center mb-2">
      <p className="text-white font-medium text-sm md:text-base">{name}</p>
      <span className="text-sm font-semibold" style={{ color: C.gold }}>{score}/10</span>
    </div>
    <div className="h-2 rounded-full flex" style={{ background: '#333' }}>
      <div className="h-full rounded-full" style={{ width: `${score * 10}%`, background: `linear-gradient(90deg, ${C.gold}, ${C.goldLight})` }} />
    </div>
    <p className="text-xs mt-2 leading-relaxed" style={{ color: '#888' }}>{desc}</p>
  </div>
);

/* ─── Trait Tag ─── */
const TraitTag = ({ label, color }) => (
  <span className="inline-block px-3 py-1.5 rounded-md text-xs font-medium border"
    style={{ color, borderColor: `${color}40`, background: `${color}10` }}>
    {label}
  </span>
);

/* ─── Main Page ─── */
const FaceReadingComingSoon = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState('');
  const [activeTab, setActiveTab] = useState('features');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'face-reading' })
      });
      const data = await res.json();
      setSubmitMsg(data.message || "You're on the list!");
      setSubmitted(true);
      setEmail('');
    } catch {
      setSubmitMsg("Something went wrong. Please try again.");
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  /* 12 Palaces data */
  const palaces = [
    { num: 1, name: 'Life Palace', position: 'Between eyebrows', chinese: '命宫' },
    { num: 2, name: 'Career Palace', position: 'Middle forehead', chinese: '官禄宫' },
    { num: 3, name: 'Parents Palace', position: 'Upper forehead', chinese: '父母宫' },
    { num: 4, name: 'Siblings Palace', position: 'Eyebrow region', chinese: '兄弟宫' },
    { num: 5, name: 'Wealth Palace', position: 'Nose tip', chinese: '财帛宫' },
    { num: 6, name: 'Property Palace', position: 'Upper eyelids', chinese: '田宅宫' },
    { num: 7, name: 'Marriage Palace', position: 'Temple area', chinese: '夫妻宫' },
    { num: 8, name: 'Health Palace', position: 'Nose bridge', chinese: '疾厄宫' },
    { num: 9, name: 'Friends Palace', position: 'Lower cheeks', chinese: '奴仆宫' },
    { num: 10, name: 'Travel Palace', position: 'Temples', chinese: '迁移宫' },
    { num: 11, name: 'Children Palace', position: 'Under eyes', chinese: '子女宫' },
    { num: 12, name: 'Later Life Palace', position: 'Chin', chinese: '地阁宫' },
  ];

  const tabs = [
    { id: 'features', label: 'Features', icon: Sparkles },
    { id: 'palaces', label: '12 Palaces', icon: Crown },
    { id: 'wealth', label: 'Wealth', icon: TrendingUp },
    { id: 'destiny', label: 'Destiny', icon: Compass },
  ];

  return (
    <div className="min-h-screen" style={{ background: C.dark, color: C.cream }}>

      {/* ─── BG Pattern ─── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full opacity-[0.03]"
          style={{ background: `radial-gradient(circle, ${C.gold}, transparent 70%)` }} />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full opacity-[0.02]"
          style={{ background: `radial-gradient(circle, ${C.rose}, transparent 70%)` }} />
      </div>

      {/* ─── NAV ─── */}
      <nav className="relative z-10 border-b" style={{ borderColor: '#ffffff08' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Eye className="w-6 h-6" style={{ color: C.gold }} />
            <span className="font-semibold text-white text-lg tracking-wide">Fortune<span style={{ color: C.gold }}>FaceMap</span></span>
          </div>
          <a href="#waitlist">
            <Button className="text-sm font-medium px-5"
              style={{ background: C.gold, color: C.charcoal }}>
              Join Waitlist
            </Button>
          </a>
        </div>
      </nav>

      {/* ═══════════════ HERO ═══════════════ */}
      <section className="relative z-10 pt-20 pb-28 md:pt-28 md:pb-36" data-testid="hero-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-8"
            style={{ background: `${C.gold}12`, border: `1px solid ${C.gold}30` }}>
            <Sparkles className="w-3.5 h-3.5" style={{ color: C.gold }} />
            <span className="text-xs tracking-widest uppercase" style={{ color: C.gold }}>Coming Soon</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight mb-6">
            Your Face Tells a Story.<br className="hidden sm:block" />
            <span className="font-serif italic" style={{ color: C.goldLight }}>
              We're Building the Tool to Read It.
            </span>
          </h1>

          <p className="text-base md:text-lg max-w-2xl mx-auto mb-4 leading-relaxed" style={{ color: '#999' }}>
            Discover the ancient art of Chinese Face Reading (Mian Xiang) — decode personality,
            fortune, health, and destiny from the 12 palaces of the face.
          </p>

          <p className="text-sm max-w-xl mx-auto mb-10" style={{ color: '#777' }}>
            A modern platform bringing thousand-year-old wisdom to your fingertips. Launching soon.
          </p>

          <div className="flex flex-wrap gap-4 justify-center">
            <a href="#waitlist">
              <Button className="px-8 py-3 text-base font-medium"
                style={{ background: C.gold, color: C.charcoal }}>
                Join the Waitlist
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <a href="#waitlist">
              <Button className="px-8 py-3 text-base font-medium border"
                style={{ background: 'transparent', color: C.cream, borderColor: C.cardBorder }}>
                <Mail className="w-4 h-4 mr-2" />
                Notify Me When Live
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* ═══════════════ FACE MAP IMAGE ═══════════════ */}
      <section className="relative z-10 pb-20" data-testid="face-map-image">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${C.cardBorder}` }}>
            <img
              src="https://customer-assets.emergentagent.com/job_461f73ce-00d7-4bc6-9fca-0baf1a22099b/artifacts/uthhfyku_Screen%20Shot%202026-03-18%20at%204.34.20%20pm.png"
              alt="Chinese Face Reading - 12 Palaces Map"
              className="w-full h-auto"
            />
          </div>
          <p className="text-center text-xs mt-4" style={{ color: '#666' }}>
            The 12 Palaces of Chinese Face Reading — Each zone reveals a different aspect of your fortune and destiny.
          </p>
        </div>
      </section>

      {/* ═══════════════ DASHBOARD PREVIEW ═══════════════ */}
      <section className="relative z-10 pb-24" data-testid="dashboard-preview">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-widest mb-4" style={{ color: C.gold }}>Platform Preview</p>
            <h2 className="text-3xl md:text-4xl font-light text-white">
              What the <span className="font-serif italic" style={{ color: C.goldLight }}>Dashboard</span> Looks Like
            </h2>
          </div>

          <div className="rounded-2xl p-1" style={{ background: `linear-gradient(135deg, ${C.gold}25, ${C.cardBorder}, ${C.rose}15)` }}>
            <div className="rounded-xl overflow-hidden" style={{ background: C.dark }}>

              {/* Tab Bar */}
              <div className="flex border-b" style={{ borderColor: C.cardBorder }}>
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className="flex-1 flex items-center justify-center gap-2 py-4 px-4 text-sm font-medium transition-all relative"
                    style={{
                      color: activeTab === tab.id ? C.cream : '#666',
                      background: activeTab === tab.id ? `${C.card}` : 'transparent',
                    }}
                    data-testid={`tab-${tab.id}`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                    {activeTab === tab.id && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: C.gold }} />
                    )}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="p-6 md:p-8">

                {/* ─── FEATURES TAB ─── */}
                {activeTab === 'features' && (
                  <div data-testid="tab-content-features">
                    {/* Face Element */}
                    <div className="rounded-xl p-6 mb-6" style={{ background: C.darkCard, border: `1px solid ${C.cardBorder}` }}>
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                        <div className="flex-1">
                          <p className="text-xs uppercase tracking-widest mb-2" style={{ color: C.gold }}>Face Element</p>
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: '#333' }}>
                              <Flame className="w-6 h-6" style={{ color: C.gold }} />
                            </div>
                            <span className="text-3xl md:text-4xl font-light text-white tracking-wide">FIRE</span>
                          </div>
                          <p className="text-sm leading-relaxed mb-5" style={{ color: '#999' }}>
                            The face shape exhibits a strong, angular structure characteristic of the Fire element.
                            This indicates assertiveness, leadership qualities, and a dynamic personality.
                            The dominant Fire traits suggest passion and a tendency to be charismatic and influential.
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {['dynamic', 'authoritative', 'motivating', 'passionate', 'charismatic'].map(tag => (
                              <TraitTag key={tag} label={tag} color={C.goldMuted} />
                            ))}
                          </div>
                        </div>
                        <div className="flex flex-col items-center">
                          <p className="text-xs uppercase tracking-widest mb-3" style={{ color: '#777' }}>Fortune Score</p>
                          <div className="w-24 h-24 rounded-full flex items-center justify-center"
                            style={{ background: `${C.green}15`, border: `2px solid ${C.green}50` }}>
                            <span className="text-3xl font-bold" style={{ color: C.greenLight }}>88</span>
                          </div>
                          <p className="text-xs mt-2" style={{ color: C.greenLight }}>out of 100</p>
                        </div>
                      </div>
                    </div>

                    {/* Personality Profile */}
                    <div className="rounded-xl p-6" style={{ background: C.darkCard, border: `1px solid ${C.cardBorder}` }}>
                      <h3 className="text-lg font-serif italic mb-6" style={{ color: C.goldLight }}>Personality Profile</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Dominant Traits */}
                        <div>
                          <p className="text-xs uppercase tracking-widest mb-4" style={{ color: '#777' }}>Dominant Traits</p>
                          <div className="flex flex-wrap gap-2">
                            {['confident', 'assertive', 'determined', 'charismatic', 'passionate'].map(t => (
                              <TraitTag key={t} label={t} color={C.goldMuted} />
                            ))}
                          </div>
                        </div>
                        {/* Strengths */}
                        <div>
                          <p className="text-xs uppercase tracking-widest mb-4" style={{ color: '#777' }}>Strengths</p>
                          <div className="flex flex-wrap gap-2">
                            {['leadership', 'strong communication', 'problem-solving', 'motivational', 'ambitious'].map(t => (
                              <TraitTag key={t} label={t} color={C.green} />
                            ))}
                          </div>
                        </div>
                        {/* Growth Areas */}
                        <div>
                          <p className="text-xs uppercase tracking-widest mb-4" style={{ color: '#777' }}>Growth Areas</p>
                          <div className="flex flex-wrap gap-2">
                            {['patience', 'emotional regulation', 'stress management'].map(t => (
                              <TraitTag key={t} label={t} color={C.rose} />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ─── 12 PALACES TAB ─── */}
                {activeTab === 'palaces' && (
                  <div data-testid="tab-content-palaces">
                    <h3 className="text-lg font-serif italic mb-6" style={{ color: C.goldLight }}>12 Palaces Legend</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {palaces.map((p, i) => (
                        <div key={p.num} className="flex items-center gap-4 p-4 rounded-xl"
                          style={{ background: C.darkCard, border: `1px solid ${C.cardBorder}` }}>
                          <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
                            style={{ background: palaceBadgeColors[i], color: '#1a1a1a' }}>
                            {p.num}
                          </div>
                          <div>
                            <p className="text-white text-sm font-medium">{p.name}</p>
                            <p className="text-xs" style={{ color: '#888' }}>{p.position}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ─── WEALTH TAB ─── */}
                {activeTab === 'wealth' && (
                  <div data-testid="tab-content-wealth">
                    <div className="flex items-center justify-between mb-8">
                      <h3 className="text-lg font-serif italic" style={{ color: C.goldLight }}>Wealth Potential</h3>
                      <span className="px-3 py-1 rounded text-xs font-medium uppercase tracking-wider"
                        style={{ border: `1px solid ${C.gold}40`, color: C.gold }}>High</span>
                    </div>
                    <WealthBar name="Nose Fullness (Wealth Palace 财帛宫)" score={9}
                      desc="The nose is fleshy with wide wings and a well-defined tip, indicating a strong capacity for wealth. The nostrils are not exposed, suggesting substantial financial potential and stability." />
                    <WealthBar name="Cheek Support" score={8}
                      desc="The cheeks are slightly fleshy, providing good support for the nose, which indicates the presence of prosperity and supportive relationships that enhance financial stability." />
                    <WealthBar name="Chin & Jaw Stability" score={8}
                      desc="The broader chin reflects a strong foundation for later-life support, indicating good potential for security in retirement and family stability." />
                    <WealthBar name="Lip & Mouth Definition" score={7}
                      desc="The mouth is moderately defined, which indicates the ability to find enjoyment in life. Clear lip borders suggest positive interactions with others while projecting a favorable image." />
                    <WealthBar name="Forehead Prosperity" score={8}
                      desc="The high and broad forehead reflects ambition and the potential to achieve wealth through strategic thinking and hard work, promoting good fortune in career prospects." />
                  </div>
                )}

                {/* ─── DESTINY TAB ─── */}
                {activeTab === 'destiny' && (
                  <div data-testid="tab-content-destiny">
                    <h3 className="text-lg font-serif italic mb-6" style={{ color: C.goldLight }}>Fortune by Age Period</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                      {[
                        { age: '15–30', zone: 'Forehead', fortune: 'Rising', desc: 'Career foundation building', pct: 65 },
                        { age: '31–34', zone: 'Brows', fortune: 'Peak', desc: 'Authority & recognition', pct: 85 },
                        { age: '35–40', zone: 'Eyes', fortune: 'Strong', desc: 'Vision & partnerships', pct: 78 },
                        { age: '41–50', zone: 'Nose', fortune: 'Wealth', desc: 'Peak earning power', pct: 90 },
                        { age: '51–60', zone: 'Mouth', fortune: 'Stable', desc: 'Enjoyment & harvest', pct: 70 },
                        { age: '61–75', zone: 'Chin', fortune: 'Settled', desc: 'Legacy & security', pct: 60 },
                      ].map(p => (
                        <div key={p.age} className="rounded-xl p-5"
                          style={{ background: C.darkCard, border: `1px solid ${C.cardBorder}` }}>
                          <p className="text-xs font-medium mb-1" style={{ color: C.gold }}>{p.age}</p>
                          <p className="text-white font-medium text-lg">{p.zone}</p>
                          <p className="text-xs mb-3" style={{ color: '#777' }}>{p.desc}</p>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 rounded-full" style={{ background: '#333' }}>
                              <div className="h-full rounded-full" style={{ width: `${p.pct}%`, background: C.gold }} />
                            </div>
                            <span className="text-xs font-medium" style={{ color: C.goldLight }}>{p.fortune}</span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Key Insight */}
                    <div className="rounded-xl p-6" style={{ background: C.darkCard, border: `1px solid ${C.cardBorder}` }}>
                      <p className="text-xs uppercase tracking-widest mb-3" style={{ color: '#777' }}>Destiny Summary</p>
                      <p className="text-sm leading-relaxed" style={{ color: '#bbb' }}>
                        Strong <span className="text-white font-medium">Career Palace</span> with high forehead indicates
                        leadership potential and authority. The prominent <span style={{ color: C.gold }}>Wealth Palace</span> suggests
                        strong financial accumulation ability in the 40s–50s age range. Combined with
                        the <span className="text-white font-medium">Fire element</span> face shape, this profile points to
                        a dynamic, action-oriented individual who achieves through charisma and strategic timing.
                      </p>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ FEATURES ═══════════════ */}
      <section className="relative z-10 py-24" data-testid="features-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest mb-4" style={{ color: C.gold }}>Platform Features</p>
            <h2 className="text-3xl md:text-4xl font-light text-white">
              Powerful <span className="font-serif italic" style={{ color: C.goldLight }}>Face Reading</span> Intelligence
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard icon={Eye} title="12 Palaces Analysis"
              desc="Complete mapping of all 12 fortune palaces on the face — Career, Wealth, Life, Spouse, Health, Property, Children, and more."
              accent={C.gold} />
            <FeatureCard icon={TrendingUp} title="Fortune Timeline"
              desc="See how your fortune changes across different life stages — from youth (forehead) through middle age (nose) to later years (chin)."
              accent={C.green} />
            <FeatureCard icon={Crown} title="Wealth Potential"
              desc="Detailed analysis of your wealth indicators: nose fullness, cheek support, chin stability, lip definition, and forehead prosperity."
              accent={C.goldLight} />
            <FeatureCard icon={Compass} title="Five Officers Reading"
              desc="Analyse the Five Officers — eyebrows, eyes, nose, mouth, and ears — to reveal your innate talents and character strengths."
              accent={C.rose} />
            <FeatureCard icon={Flame} title="Face Element Type"
              desc="Discover whether your face shape is Wood, Fire, Earth, Metal, or Water — and how it shapes your personality and destiny."
              accent="#E85D47" />
            <FeatureCard icon={Star} title="Personality Profile"
              desc="Uncover dominant traits, natural strengths, and growth areas through classical Mian Xiang facial structure analysis."
              accent={C.greenLight} />
          </div>
        </div>
      </section>

      {/* ═══════════════ PHILOSOPHY ═══════════════ */}
      <section className="relative z-10 py-24" data-testid="about-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs uppercase tracking-widest mb-4" style={{ color: C.goldMuted }}>Philosophy</p>
          <h2 className="text-3xl md:text-4xl font-light text-white mb-8">
            Ancient Wisdom, <span className="font-serif italic" style={{ color: C.goldLight }}>Modern Clarity</span>
          </h2>
          <p className="text-base leading-relaxed mb-6" style={{ color: '#aaa' }}>
            <span className="text-white font-medium">Mian Xiang (面相)</span> — Chinese Face Reading — is one of the oldest
            branches of Chinese Metaphysics. For thousands of years, imperial advisors used facial analysis to assess character,
            predict fortune, and guide strategic decisions.
          </p>
          <p className="text-base leading-relaxed" style={{ color: '#aaa' }}>
            Our platform transforms this classical art into a <span className="text-white font-medium">modern analytical tool</span>,
            making face reading insights accessible, structured, and actionable for everyday life.
          </p>
        </div>
      </section>

      {/* ═══════════════ COUNTDOWN + WAITLIST ═══════════════ */}
      <section className="relative z-10 py-28" id="waitlist" data-testid="waitlist-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs uppercase tracking-widest mb-4" style={{ color: C.gold }}>Launching Soon</p>
          <h2 className="text-3xl md:text-4xl font-light text-white mb-10">
            The Future of <span className="font-serif italic" style={{ color: C.goldLight }}>Face Reading</span>
          </h2>

          <CountdownTimer />

          <p className="mt-10 mb-8 text-base" style={{ color: '#999' }}>
            We are building something powerful for the Face Reading community.<br />
            Join the waitlist to get early access.
          </p>

          {submitted ? (
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-xl"
              style={{ background: `${C.green}15`, border: `1px solid ${C.green}30`, color: C.greenLight }}>
              <Star className="w-5 h-5" />
              <span className="font-medium">{submitMsg}</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="waitlist-form">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 h-12 text-white border-0"
                style={{ background: C.card, borderColor: C.cardBorder }}
                data-testid="waitlist-email"
              />
              <Button type="submit" disabled={submitting} className="h-12 px-8 font-medium"
                style={{ background: C.gold, color: C.charcoal }}
                data-testid="waitlist-submit">
                {submitting ? 'Joining...' : 'Join Waitlist'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* ═══════════════ FOOTER ═══════════════ */}
      <footer className="relative z-10 border-t py-12" style={{ borderColor: '#ffffff08' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <div className="flex items-center gap-2 justify-center md:justify-start mb-1">
                <Eye className="w-5 h-5" style={{ color: C.gold }} />
                <span className="text-white font-semibold">FortuneFaceMap</span>
              </div>
              <p className="text-xs" style={{ color: '#666' }}>Powered by Life Decoder</p>
            </div>
            <div className="flex items-center gap-6">
              {['About', 'Contact', 'Privacy Policy'].map((link) => (
                <a key={link} href="#" className="text-sm transition-colors hover:text-white" style={{ color: '#777' }}>{link}</a>
              ))}
            </div>
          </div>

          <div className="mt-8 pt-6 flex flex-wrap justify-center gap-4" style={{ borderTop: '1px solid #ffffff08' }}>
            {[
              { name: 'Stevenyong.live', url: 'https://stevenyong.live' },
              { name: 'Bazi.live', url: 'https://bazi.live' },
              { name: 'QMDJ.live', url: 'https://qmdj.live' },
              { name: 'Fengshuiplotter.live', url: 'https://fengshuiplotter.live' },
              { name: 'Lifecode.live', url: 'https://lifecode.live' },
              { name: 'Lifetunes.live', url: 'https://lifetunes.live' },
              { name: 'Onlinetongshu.com', url: 'https://onlinetongshu.com' },
            ].map((link) => (
              <a key={link.name} href={link.url} target="_blank" rel="noopener noreferrer"
                className="text-xs transition-colors hover:text-white" style={{ color: C.gold }}>
                {link.name}
              </a>
            ))}
          </div>
          <div className="mt-8 pt-6 text-center" style={{ borderTop: '1px solid #ffffff08' }}>
            <p className="text-xs" style={{ color: '#555' }}>Life Decoder &copy; {new Date().getFullYear()} FortuneFaceMap - All rights reserved</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FaceReadingComingSoon;
